import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MapPin, Search, ArrowRight, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { worldCountries } from '@/data/countries/index';

interface CityNotFoundFallbackProps {
  citySlug: string;
  countryCode?: string;
}

const CityNotFoundFallback: React.FC<CityNotFoundFallbackProps> = ({ citySlug, countryCode }) => {
  const navigate = useNavigate();

  // Find nearby/suggested cities
  const getSuggestedCities = () => {
    const allCities: Array<{ name: string; slug: string; country: string; population: number }> = [];
    
    worldCountries.forEach(country => {
      country.topCities.slice(0, 5).forEach(city => {
        allCities.push({
          name: city.name,
          slug: city.slug,
          country: country.name,
          population: city.population
        });
      });
    });

    // Sort by population and return top 6
    return allCities
      .sort((a, b) => b.population - a.population)
      .slice(0, 6);
  };

  const suggestedCities = getSuggestedCities();
  const cityName = citySlug.split('-').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');

  return (
    <>
      <Helmet>
        <title>City Not Available - Global Therapy Hub</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-gradient-subtle py-16">
        <div className="container max-w-4xl mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-muted mb-6">
              <MapPin className="w-10 h-10 text-muted-foreground" />
            </div>
            <h1 className="text-4xl font-bold mb-4">
              We're Expanding to {cityName}
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              We're working on adding therapists in this area. In the meantime, explore these available cities or search our global directory.
            </p>
          </div>

          {/* Quick Actions */}
          <div className="grid md:grid-cols-2 gap-4 mb-12">
            <Button 
              size="lg" 
              className="w-full"
              onClick={() => navigate('/find')}
            >
              <Search className="w-5 h-5 mr-2" />
              Search All Therapists
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="w-full"
              onClick={() => navigate('/cities')}
            >
              <Home className="w-5 h-5 mr-2" />
              View All Cities
            </Button>
          </div>

          {/* Suggested Cities */}
          <Card>
            <CardHeader>
              <CardTitle>Explore Available Cities</CardTitle>
              <CardDescription>
                Browse therapists in these popular locations with active professionals
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                {suggestedCities.map((city) => (
                  <button
                    key={city.slug}
                    onClick={() => navigate(`/city/${city.slug}`)}
                    className="flex items-center justify-between p-4 rounded-lg border border-border hover:border-primary hover:bg-accent transition-all text-left group"
                  >
                    <div>
                      <h3 className="font-semibold group-hover:text-primary transition-colors">
                        {city.name}
                      </h3>
                      <p className="text-sm text-muted-foreground">{city.country}</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Info Section */}
          <div className="mt-12 text-center">
            <h2 className="text-2xl font-semibold mb-4">
              Help Us Grow Our Network
            </h2>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              We're constantly expanding our global directory of certified therapists. 
              If you're a mental health professional in {cityName}, we'd love to have you join our platform.
            </p>
            <Button 
              variant="outline" 
              size="lg"
              onClick={() => navigate('/for-therapists')}
            >
              Join as a Therapist
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CityNotFoundFallback;
